import api from '../services/api';

export const PUSH_STATUS_EVENT = 'push-status-changed';
const publishStatus = (status) => window.dispatchEvent(new CustomEvent(PUSH_STATUS_EVENT, { detail: status }));

export function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(window.atob(base64), (char) => char.charCodeAt(0));
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

const sameKey = (current, expected) => {
  if (!current) return true;
  const bytes = new Uint8Array(current);
  return bytes.length === expected.length && bytes.every((value, index) => value === expected[index]);
};

async function publicKey() {
  if (process.env.REACT_APP_VAPID_PUBLIC_KEY) return process.env.REACT_APP_VAPID_PUBLIC_KEY;
  const response = await api.get('/push/public-key');
  return response.data?.publicKey || '';
}

export async function subscribeToPush() {
  if (!isPushSupported() || localStorage.getItem('impersonateOrg')) return null;
  if ((await Notification.requestPermission()) !== 'granted') {
    publishStatus(Notification.permission === 'denied' ? 'denied' : 'available');
    return null;
  }

  const key = await publicKey();
  if (!key) throw new Error('Push notifications are not configured yet.');
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) throw new Error('Reload the app before enabling notifications.');

  const applicationServerKey = urlBase64ToUint8Array(key);
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameKey(subscription.options?.applicationServerKey, applicationServerKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }

  await api.post('/push/subscribe', {
    subscription: subscription.toJSON(),
    app: window.location.hostname
  });
  publishStatus('subscribed');
  return subscription;
}

export async function syncPushSubscription() {
  if (!isPushSupported() || Notification.permission !== 'granted') return null;
  return subscribeToPush();
}

export async function hasActivePushSubscription() {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const registration = await navigator.serviceWorker.getRegistration();
  return Boolean(await registration?.pushManager.getSubscription());
}

export async function getPushStatus() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return (await hasActivePushSubscription()) ? 'subscribed' : 'available';
}

export async function unsubscribeFromPush({ authToken } = {}) {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return false;

  try {
    await api.post(
      '/push/unsubscribe',
      { endpoint: subscription.endpoint },
      authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined
    );
  } catch (err) {
    console.warn('Failed to remove push subscription from server:', err);
  }
  const removed = await subscription.unsubscribe();
  publishStatus('available');
  return removed;
}
